import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Badge from './Badge';
import { ArrowRight, Lock, Loader2 } from 'lucide-react';
import { getMaturityBand, getCurrencySymbol } from './utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const glassCard = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.8)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
};

const SECTION_COLOURS = ['#3b82f6', '#7c3aed', '#f59e0b'];
const SECTION_NAMES = ['Customer Intelligence', 'Communication Execution', 'Omni-Channel Loyalty & Offers'];

const BLOCKED_DOMAINS = [
  'gmail.com', 'yahoo.com', 'yahoo.co.in', 'hotmail.com', 'outlook.com',
  'live.com', 'msn.com', 'aol.com', 'icloud.com', 'me.com', 'mail.com',
  'protonmail.com', 'proton.me', 'yandex.com', 'zoho.com', 'gmx.com',
  'rediffmail.com', 'tutanota.com',
];

const validateEmail = (email) => {
  if (!email || !email.includes('@') || !email.includes('.') || email.includes(' ')) return false;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return !BLOCKED_DOMAINS.includes(domain);
};

const deriveCompanyName = (email) => {
  if (!email || !email.includes('@')) return '';
  const domain = email.split('@')[1];
  if (!domain) return '';
  const name = domain.split('.')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const computeRevenueOpportunity = (businessInputs) => {
  const { monthlyOrders, aov, country, repeatSalesPct, dataCollectionPct } = businessInputs;
  if (!monthlyOrders || !aov) return '0';
  const annualRevenue = monthlyOrders * aov * 12;
  const upliftPct = Math.max(0, (100 - (repeatSalesPct || 40)) * 0.001 + (100 - (dataCollectionPct || 60)) * 0.0005 + 0.08);
  const opportunity = annualRevenue * upliftPct;
  const sym = getCurrencySymbol(country);
  if (country === 'India') {
    return `${sym}${Math.round(opportunity / 10000000)} Cr`;
  }
  if (opportunity >= 1000000) {
    return `${sym}${Math.round(opportunity / 1000000)}M`;
  }
  return `${sym}${Math.round(opportunity).toLocaleString('en-IN')}`;
};

const GateScreen = ({ onNext, onBack, gateData, setGateData, scores, answers, businessInputs }) => {
  const [email, setEmail] = useState(gateData.email || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [animatedBars, setAnimatedBars] = useState(false);
  const emailRef = useRef(null);

  const finalScore = scores?.final || 0;
  const sectionScores = scores?.sections || [0, 0, 0];
  const maturityBand = getMaturityBand(finalScore);
  const revenueEstimate = computeRevenueOpportunity(businessInputs);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedBars(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const handleEmailChange = (val) => {
    let cleaned = val.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
    setEmail(cleaned);
    if (error) setError('');
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      setError('Please enter a valid work email — e.g. name@yourcompany.com');
      return;
    }

    setLoading(true);
    const companyName = deriveCompanyName(email);
    setGateData({ email, companyName });

    let resultId = null;

    try {
      // Save results to get shareable link
      const saveRes = await fetch(`${BACKEND_URL}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, companyName,
          scores: { sections: sectionScores, final: finalScore },
          maturityBand,
          businessInputs: {
            monthlyOrders: businessInputs.monthlyOrders, aov: businessInputs.aov,
            country: businessInputs.country, industry: businessInputs.industry,
            dataCollectionPct: businessInputs.dataCollectionPct, repeatSalesPct: businessInputs.repeatSalesPct,
          },
          answers,
        }),
      });
      const saveData = await saveRes.json();
      if (saveData.ok) resultId = saveData.result_id;
    } catch {
      // Continue without shareable link
    }

    // Fire-and-forget email notification
    try {
      fetch(`${BACKEND_URL}/api/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, companyName,
          scores: { sections: sectionScores, final: finalScore },
          maturityBand,
          businessInputs: {
            monthlyOrders: businessInputs.monthlyOrders, aov: businessInputs.aov,
            country: businessInputs.country, industry: businessInputs.industry,
            dataCollectionPct: businessInputs.dataCollectionPct, repeatSalesPct: businessInputs.repeatSalesPct,
          },
          answers,
        }),
      }).catch(() => {});
    } catch {
      // fire and forget
    }

    setTimeout(() => {
      setLoading(false);
      onNext(resultId);
    }, 600);
  };

  return (
    <div className="flex flex-col items-center gap-5 w-full" data-testid="gate-screen">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }} className="w-full rounded-2xl p-6 sm:p-8" style={glassCard}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs font-semibold text-[#3b82f6] tracking-wide uppercase">Your audit is complete</p>
            <h2 className="text-[22px] font-bold text-[#0f172a] mt-1">Here is a preview of your results</h2>
          </div>
          <Badge level={maturityBand} />
        </div>

        <div className="flex flex-col gap-4">
          {sectionScores.map((score, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <span className="text-[13px] font-medium text-[#334155] w-[220px] flex-shrink-0">{SECTION_NAMES[idx]}</span>
              <div className="flex-1 h-2.5 rounded-full overflow-hidden bg-[#e2e8f0]">
                <motion.div initial={{ width: 0 }} animate={{ width: animatedBars ? `${score * 10}%` : 0 }} transition={{ duration: 0.6, delay: 0.12 * idx }} className="h-full rounded-full" style={{ backgroundColor: SECTION_COLOURS[idx] }} />
              </div>
              <span className="text-[13px] font-bold w-[65px] text-right flex-shrink-0" style={{ color: SECTION_COLOURS[idx] }}>{Math.round(score * 10)}/100</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between rounded-xl px-5 py-4" style={{ background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.1)' }}>
          <div>
            <p className="text-xs font-medium text-[#64748b]">Estimated annual revenue opportunity</p>
            <p className="text-[22px] font-bold text-[#0f172a] mt-0.5 select-none" style={{ filter: 'blur(6px)' }}>{revenueEstimate}</p>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-[18px] h-[18px] text-[#94a3b8]" />
            <span className="text-xs font-medium text-[#94a3b8]">Enter your email to unlock</span>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 }} className="w-full rounded-2xl p-6 sm:p-8" style={glassCard}>
        <h3 className="text-lg font-bold text-[#0f172a] mb-1.5">One step to unlock your full report</h3>
        <p className="text-sm text-[#64748b] mb-6">See personalised recommendations for each gap area.</p>
        <div className="mb-5">
          <label className="block text-[13px] font-semibold text-[#334155] mb-1.5">Your official email ID</label>
          <input
            ref={emailRef} type="email" value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="email@yourcompany.com"
            className="w-full h-11 px-3 text-sm font-medium text-[#0f172a] placeholder-[#94a3b8] rounded-lg border focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all"
            style={{ background: 'rgba(255,255,255,0.9)', borderColor: error ? '#ef4444' : '#e2e8f0' }}
            data-testid="gate-email-input"
          />
          {error && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-[#ef4444] mt-1.5 font-medium">{error}</motion.p>}
        </div>
        <motion.button
          onClick={handleSubmit} disabled={loading}
          whileHover={!loading ? { scale: 1.02 } : {}} whileTap={!loading ? { scale: 0.97 } : {}}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white text-sm font-semibold rounded-full shadow-lg transition-all disabled:opacity-80 cursor-pointer"
          data-testid="gate-unlock-button"
        >
          {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending…</>) : (<>Unlock my results<ArrowRight className="w-4 h-4" /></>)}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default GateScreen;
